import { Box, Checkbox } from "@mui/material";
import {
  Cancel as CancelIcon,
  TrendingFlat as TrendingFlatIcon,
  Person as PersonIcon,
  ArrowDropUp,
  ArrowDropDown,
} from "@mui/icons-material";
import { t } from "./coachTokens";
import ClientAvatar from "./ClientAvatar";
import FilterChip from "./FilterChip";
import { GOAL_CUT, GOAL_GAIN, isGoalCut, isGoalGain, goalAwareTrendColor } from "./coachHelpers";

// Re-exported from the shared helpers so existing imports of these names from WeightTab
// keep working; trendColor is the goal-aware color (WeightTab's original name for it).
export { GOAL_CUT, GOAL_GAIN, isGoalCut, isGoalGain };
export const trendColor = goalAwareTrendColor;

export const trendStr = (trend) => {
  if (trend == null) return "—";
  if (trend === 0) return "0";
  const abs = Math.abs(trend).toFixed(1);
  return trend > 0 ? `↑ ${abs}` : `↓ ${abs}`;
};

// trend ∈ [min, max] given the WeeklyRange bounds (which can be negative for loss goals).
export const trendMetGoal = (r) => {
  if (r.weightTrend == null || r.weightTargetMin == null || r.weightTargetMax == null) return null;
  const lo = Math.min(r.weightTargetMin, r.weightTargetMax);
  const hi = Math.max(r.weightTargetMin, r.weightTargetMax);
  return r.weightTrend >= lo && r.weightTrend <= hi;
};

export const goalLabel = (r) => {
  if (!r.goal) return null;
  if (r.weightTargetMin == null || r.weightTargetMax == null) {
    if (r.goal === "MAINTENANCE") return "ריקומפ";
    return null;
  }
  if (isGoalCut(r.goal)) {
    const lo = Math.abs(Math.max(r.weightTargetMin, r.weightTargetMax));
    const hi = Math.abs(Math.min(r.weightTargetMin, r.weightTargetMax));
    return `ירידה ${lo}-${hi} ק"ג`;
  }
  if (isGoalGain(r.goal)) {
    const lo = Math.min(r.weightTargetMin, r.weightTargetMax);
    const hi = Math.max(r.weightTargetMin, r.weightTargetMax);
    return `עלייה ${lo}-${hi} ק"ג`;
  }
  if (r.goal === "MAINTENANCE") return "ריקומפ";
  return null;
};

export const WeightFilters = ({ filter, onChange }) => (
  <>
    <FilterChip
      active={filter === "noWeighIns"}
      onClick={() => onChange(filter === "noWeighIns" ? "all" : "noWeighIns")}
      color={t.red}
      label={
        <>
          <CancelIcon sx={{ fontSize: 12 }} /> לא נשקלו השבוע
        </>
      }
    />
    <FilterChip
      active={filter === "missedGoal"}
      onClick={() => onChange(filter === "missedGoal" ? "all" : "missedGoal")}
      color={t.t2}
      label={
        <>
          <TrendingFlatIcon sx={{ fontSize: 12 }} /> לא עמדו ביעד
        </>
      }
    />
  </>
);

const cell = { padding: "12px 14px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", verticalAlign: "middle", textAlign: "center" };
const thBase = {
  textAlign: "center",
  padding: "10px 14px",
  fontSize: "10.5px",
  fontWeight: 700,
  color: t.t3,
  borderBottom: `1px solid ${t.border}`,
  background: t.bg,
  whiteSpace: "nowrap",
  textTransform: "uppercase",
  letterSpacing: ".06em",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const SortableTh = ({ label, sortKey, currentKey, currentDir, onSortChange, align = "center", width }) => {
  const active = currentKey === sortKey;
  return (
    <Box
      component="th"
      onClick={() => onSortChange(sortKey)}
      sx={{
        ...thBase,
        textAlign: align,
        width,
        cursor: "pointer",
        userSelect: "none",
        color: active ? t.text : t.t3,
        "&:hover": { color: t.text },
      }}
    >
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: "2px", justifyContent: align === "center" ? "center" : "flex-start" }}>
        {label}
        <Box sx={{ display: "inline-flex", flexDirection: "column", lineHeight: 0.6, opacity: active ? 1 : 0.35 }}>
          <ArrowDropUp sx={{ fontSize: 14, color: active && currentDir === "ASC" ? t.green : t.t3, mb: "-4px" }} />
          <ArrowDropDown sx={{ fontSize: 14, color: active && currentDir === "DESC" ? t.green : t.t3, mt: "-4px" }} />
        </Box>
      </Box>
    </Box>
  );
};

const GoalPill = ({ r }) => {
  if (!r.goal) {
    return <Box component="span" sx={{ color: t.t3 }}>–</Box>;
  }
  if (r.weightTrend == null) {
    return (
      <Box component="span" sx={{ display: "inline-block", background: t.bg, color: t.t3, fontSize: 11, fontWeight: 500, padding: "2px 10px", borderRadius: "20px", border: `1px dashed ${t.border}` }}>
        אין נתון
      </Box>
    );
  }
  const met = trendMetGoal(r);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        background: met ? t.greenLight : t.redLight,
        color: met ? t.greenText : t.redText,
        fontSize: 11,
        fontWeight: 600,
        padding: "2px 10px",
        borderRadius: "20px",
      }}
    >
      {met ? "✓ עמד" : "✗ לא עמד"}
    </Box>
  );
};

const WeightTab = ({ rows, selected, onToggleSelect, onToggleAll, sortKey, sortDir, onSortChange, onOpenProfile, onOpenActions }) => {
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.traineeId));

  if (rows.length === 0) {
    return (
      <Box sx={{ padding: "48px", textAlign: "center", color: t.t3, fontSize: "13px" }}>
        אין לקוחות להצגה
      </Box>
    );
  }

  return (
    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <Box component="th" sx={{ ...thBase, width: 34 }}>
            <Checkbox
              size="small"
              checked={allChecked}
              onChange={(e) => onToggleAll(rows, e.target.checked)}
              sx={{ p: 0 }}
            />
          </Box>
          <SortableTh align="start" label="שם לקוח" sortKey="name" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="ממוצע שבועי" sortKey="weightAvg" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="שבוע שעבר" sortKey="weightLastWeek" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="מגמה" sortKey="weightTrend" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <Box component="th" sx={thBase}>יעד שבועי</Box>
          <Box component="th" sx={thBase}>עמידה ביעד</Box>
          <Box component="th" sx={thBase}>פעולות</Box>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const isChecked = selected.has(r.traineeId);
          const hasAvg = r.weightAvg != null;
          const hasLast = r.weightLastWeek != null;
          const label = goalLabel(r);
          return (
            <Box
              component="tr"
              key={r.traineeId}
              onClick={() => onOpenProfile?.(r)}
              sx={{
                cursor: "pointer",
                transition: "background .1s",
                "&:hover td": { background: t.greenLight },
              }}
            >
              <Box component="td" sx={cell} onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  size="small"
                  checked={isChecked}
                  onChange={(e) => onToggleSelect(r.traineeId, e.target.checked)}
                  sx={{ p: 0 }}
                />
              </Box>
              <Box component="td" sx={{ ...cell, textAlign: "start" }}>
                <ClientAvatar name={r.traineeName} photoUrl={r.photoUrl} email={r.email} phone={r.phone} />
              </Box>
              <Box component="td" sx={cell}>
                {hasAvg ? (
                  <Box>
                    <Box component="strong" sx={{ color: t.text }}>{r.weightAvg.toFixed(1)} ק"ג</Box>
                    <Box sx={{ fontSize: 10, color: t.t3, marginTop: "2px" }}>
                      {(r.weighInsThisWeek ?? 0)}/7 שקילות
                    </Box>
                  </Box>
                ) : (
                  <Box component="span" sx={{ color: t.t3 }}>אין נתון</Box>
                )}
              </Box>
              <Box component="td" sx={cell}>
                {hasLast ? (
                  <Box>
                    <Box component="span" sx={{ color: t.t2 }}>{r.weightLastWeek.toFixed(1)} ק"ג</Box>
                    <Box sx={{ fontSize: 10, color: t.t3, marginTop: "2px" }}>
                      {(r.weighInsLastWeek ?? 0)}/7 שקילות
                    </Box>
                  </Box>
                ) : (
                  <Box component="span" sx={{ color: t.t3 }}>–</Box>
                )}
              </Box>
              <Box component="td" sx={cell}>
                <Box component="span" sx={{ color: trendColor(r.goal, r.weightTrend), fontWeight: 700 }}>
                  {trendStr(r.weightTrend)}
                </Box>
              </Box>
              <Box component="td" sx={cell}>
                {label ? (
                  <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, color: t.t2 }}>
                    {label}
                  </Box>
                ) : (
                  <Box component="span" sx={{ color: t.t3, fontSize: 11 }}>–</Box>
                )}
              </Box>
              <Box component="td" sx={cell}>
                <GoalPill r={r} />
              </Box>
              <Box component="td" sx={cell} onClick={(e) => e.stopPropagation()}>
                <Box
                  component="button"
                  onClick={(e) => onOpenActions?.(e.currentTarget, r)}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "7px",
                    border: `1.5px solid ${t.border}`,
                    background: t.bg,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: t.t2,
                    "&:hover": { background: t.greenLight, borderColor: t.green, color: t.green },
                  }}
                >
                  <PersonIcon sx={{ fontSize: 14 }} />
                </Box>
              </Box>
            </Box>
          );
        })}
      </tbody>
    </Box>
  );
};

export default WeightTab;
